import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  CircularProgress, Dialog,
  DialogTitle,
  DialogContent,
  DialogActions, Button, Switch, Menu, MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import Layout from "../../layout/layout";
import { api } from "../../services/api";
import { useToast } from "../../components/ToastContext";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FileX } from "lucide-react";
import FilterListIcon from "@mui/icons-material/FilterList";
import { BorderColor } from "@mui/icons-material";
import RoleGuard from "../../components/RoleGuard";
import { hasAction } from '../../Utils/role';


const TemplateGallery = () => {
  // const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const resourceId = searchParams.get("resourceId");
  const provider = searchParams.get("provider");
  const resourceName = searchParams.get("resourceName");

  const navigate = useNavigate();
  const toast = useToast();

  const selectedProvider = provider;

  const [search, setSearch] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(new Set());
  const [filteredList, setfilteredList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all"); // active | inactive | all
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const userId = sessionStorage.getItem('userId');

  const canChangeTemplateStatus = hasAction("CAN_CHANGE_TEMPLATE_STATUS");
  /* ---------------- SAFETY GUARD ---------------- */

  useEffect(() => {
    if (!resourceId) {
      toast.error("Invalid URL. Resource ID missing.");
      navigate(-1);
      return;
    }

    fetchTemplates();
  }, [resourceId, selectedProvider]);


  /* ---------------- FETCH ---------------- */
const fetchTemplates = async () => {
  // 1. Get the ID from Local Storage
  const activeAccountId = sessionStorage.getItem("activeAccountId");

  if (!activeAccountId) {
    console.warn("activeAccountId not found in localStorage");
    return;
  }

  setLoading(true);
  try {
    const res = await api.get(
      // 3. Append the ID as a query parameter
      `/api/Resources/GetResourceTemplatesbyResourceType/${resourceId}?activeAccountId=${activeAccountId}`,
      {
        headers: {
          "X-CORRELATION-ID": crypto.randomUUID(),
          accept: "application/json",
        },
      }
    );

    const providerMap = {
      1: "Azure",
      2: "AWS",
      3: "GCP",
    };

    const normalized = (res.data || [])
      .filter(
        (t) =>
          providerMap[t.cloudProvider]?.toLowerCase() === selectedProvider?.toLowerCase() &&
          (t.isDeleted === false || t.isDeleted === null) &&
          // 4. We still keep the client-side check if the API returns more than one account's data
          String(t.activeAccountId) === String(activeAccountId) 
      )
      .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
      .map((t) => ({
        id: t.templateId,
        name: t.templateName,
        date: new Date(t.createdDate).toLocaleDateString(),
        owner: t.createdByName,
        isActive: t.isActive,
        fieldsJson: t.fieldsJson,
      }));

    setTemplates(normalized);
    setfilteredList(normalized.filter((t) => t.isActive === true));
  } catch (e) {
    console.error(e);
    toast.error("Failed to fetch templates");
  } finally {
    setLoading(false);
  }
};

   const parseFields = (fieldsJson) => {
  try {
    return JSON.parse(fieldsJson || "[]");
  } catch {
    return [];
  }
};

const formatLabel = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};


  /* ---------------- DELETE ---------------- */
  const handleDelete = (e, id, name) => {
    e.stopPropagation();
    setSelectedItem({ id, name });
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    const { id, name } = selectedItem;

    setOpenDialog(false);
    setDeleting((p) => new Set(p).add(id));
    try {
      await api.post(
        `/api/Resources/UpdateTemplateStatus?templateId=${id}&userId=${userId}&isDeleted=D`,
        { templateId: id, userId, isDeleted: "D" },
        {
          headers: {
            "X-CORRELATION-ID": crypto.randomUUID(),
            "Content-Type": "application/json",
          },
        }
      );
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      setfilteredList((prev) => prev.filter((t) => t.id !== id))
      toast.success(`Template "${name}" deleted`);
    } catch {
      toast.error(`Failed to delete "${name}"`);
    } finally {
      setDeleting((p) => {
        const s = new Set(p);
        s.delete(id);
        return s;
      });
    }
  };

  const handleToggleStatus = async (e, id) => {
    const newStatus = e.target.checked;
    e.stopPropagation();
    setDeleting((prev) => new Set(prev).add(id));

    try {
      await api.post(
        `/api/Resources/UpdateTemplateStatus?templateId=${id}&isActive=${newStatus}&userId=${userId}&isDeleted="N"`,
        {
          templateId: id,
          isActive: newStatus,
          userId,
          isDeleted: "N"
        },
        {
          headers: {
            "X-CORRELATION-ID": crypto.randomUUID(),
          },
        }
      );

      setTemplates((prev) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, isActive: newStatus }
            : t
        )
      );
      setfilteredList((prev) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, isActive: newStatus }
            : t
        ))

      toast.success(`Template ${newStatus ? "Activated" : "Deactivated"}`);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setDeleting((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
    }
  };


  /* ---------------- SEARCH ---------------- */
  // const filteredTemplates = templates.filter((t) =>
  //   t.name.toLowerCase().includes(search.toLowerCase())
  // );

  const filteredTemplates = templates.filter((t) => {
    if (!search) {
      if (statusFilter === "inactive") return !t.isActive;
      if (statusFilter === "all") return true;
      return t.isActive;
    }
    const searchValue = search.trim().toLowerCase();

    if (searchValue.startsWith("inactive")) {
      const nameSearch = searchValue.split(":")[1]?.trim() || "";

      return (
        t.isActive === false &&
        (nameSearch === "" ||
          t.name.toLowerCase().includes(nameSearch))
      );
    }


    return t.name.toLowerCase().includes(searchValue);
  });
  const statusFilteredTemplates = filteredTemplates.filter((t) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "active") return t.isActive;
    if (statusFilter === "inactive") return !t.isActive;
    return true;
  });


  const chooseTemplate = (templateId, templateName, isActive) => {
    if (!isActive) {
      return
    }
    navigate('/Resources/Create_Request', { state: { provider, resourceId, resourceName, templateId, templateName } })
  }

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);


  useEffect(() => {
    setfilteredList(statusFilteredTemplates);
  }, [search, statusFilter, templates]);




  return (
    <Layout pageType="sideBar">
      <Box p={2}>
       <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems:'center'}}>
         <Box>
          <Typography variant="title">
          Templates – {selectedProvider}
        </Typography>

        <Typography color="text.secondary" mb={3}>
          Choose from existing templates or create a new configuration
        </Typography>
         </Box>
        
         <RoleGuard requiredPermission="CREATE_RESOURCE">
         <Button
                         size="small"
                         variant="contained"
                         onClick={() => navigate('/Resources/Create_Request', { state: { provider, resourceId, resourceName } })}
                         sx={{textTransform:"capitalize", height: 'fit-content', px:2, py:1}}>
                          Create {resourceName} 
                       </Button>
                        </RoleGuard>
              </Box>

        <Box display="flex" gap={1} mb={3}>
          <IconButton
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            sx={{
              border: "1px solid",
              borderColor: (t) => t.palette.mode === "dark" ? "divider" :  "rgba(0, 0, 0, 0.23)" ,
              borderRadius: 1,
              height: 53,
              width: 40,
              '&:hover': { borderColor: "#000" },
            }}
          >
            <FilterListIcon />
          </IconButton>
          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={() => setFilterAnchorEl(null)}
          >
            {["active", "inactive", "all"].map((status) => (
              <MenuItem
                key={status}
                selected={statusFilter === status}
                onClick={() => {
                  setStatusFilter(status);
                  setFilterAnchorEl(null);
                }}
                sx={{ textTransform: "capitalize" }}
              >
                {status}
              </MenuItem>
            ))}
          </Menu>
          <TextField
            fullWidth
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input:{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }
            }}
          />
        </Box>



        {/* Cards */}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              height: 'calc(100vh - 330px)', overflow: 'auto',
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 3,
            }}
          >
            {filteredList.map((t) => (
              <Paper
                key={t.id}
                variant="outlined"
                onClick={() => chooseTemplate(t.id, t.name, t.isActive)}
                sx={{
                  p: 2.5,
                  height: 'fit-content',
                  minHeight: '163px',
                  position: "relative",
                  cursor: t.isActive ? "pointer" : "not-allowed",
                  
                  //  Dynamically swap backgrounds for Active/Inactive states
                  bgcolor: (theme) => t.isActive 
                    ? (theme.palette.mode === 'dark' ? 'background.paper' : '#ffffff') 
                    : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'),
                  
                  borderColor: "divider", // Ensures the outline adapts to dark mode
                  
                  "&:hover .delete-btn": { opacity: 1 },
                  
                  //  Dynamically swap the hover glow effect
                  "&:hover": t.isActive ? { 
                    boxShadow: (theme) => theme.palette.mode === 'dark' 
                      ? "0px 4px 20px -5px rgba(144, 202, 249, 0.15)" // Subtle light-blue glow for dark mode
                      : "0px 4px 20px -5px #5877b0" 
                  } : {},
                }}
              >
                <RoleGuard requiredPermission="DELETE_TEMPLATE">
                <IconButton
                  className="delete-btn"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    opacity: 0,
                  }}
                  onClick={(e) => handleDelete(e, t.id, t.name)}
                >
                  {deleting.has(t.id) ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DeleteOutlineIcon fontSize="small" color="error" />
                  )}
                </IconButton>
</RoleGuard>
                <Typography fontWeight={600} color="primary">
                  {t.name}
                </Typography>
                
                <Box gap={1} mt={1}>
                  {parseFields(t.fieldsJson).map((field) => (
                    <Typography key={field.key} variant="body2" color="text.secondary">
                      {formatLabel(field.key)}: {field.value ?? "-"}
                    </Typography>
                  ))}
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <Box mt={2} onClick={(e) => e.stopPropagation()} >
                    <Box display="flex" alignItems="center" gap={1}
                    sx={{
opacity: !hasAction("CAN_CHANGE_TEMPLATE_STATUS") ? 0.5 : 1,
pointerEvents: !hasAction("CAN_CHANGE_TEMPLATE_STATUS") ? "none" : "auto",
}}
                     
                     >
                      <Switch
                        size="small"
                        checked={t.isActive}
                        onChange={(e) => handleToggleStatus(e, t.id)}
                        disabled={deleting.has(t.id)|| !canChangeTemplateStatus}
                      />
                      <Typography variant="body2" color="text.primary">
                        {t.isActive ? "Active" : "Inactive"}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box>
                    <Box display="flex" gap={1} mt={1}>
                      {/*  Replaced hardcoded #888888 with text.secondary */}
                      <CalendarTodayOutlinedIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">{t.date}</Typography>
                    </Box>

                    <Box display="flex" gap={1}>
                      {/*  Replaced hardcoded #888888 with text.secondary */}
                      <PersonOutlineOutlinedIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">{t.owner}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
         <DialogTitle sx={{ fontSize: '18px', fontWeight: 'Bold'}}>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{selectedItem?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default TemplateGallery;
